import { useShallow } from "zustand/shallow";
import {
  type AuthStatus,
  usePlaygroundStore,
} from "@/app/playground/execute/hooks/use-store";
import { checkNeedsOAuthAuthorization } from "@/hooks/use-tool-requirements-status";
import { TerminalOutput } from "./components/terminal-output";

type ResponsePanelProps = {
  onExecute?: () => void;
  isExecuteDisabled?: boolean;
  expectedOutput?: string;
  tooltipMessage?: string | null;
  onBackToTool?: () => void;
};

type TerminalState = "ready" | "loading" | "success" | "error";

function deriveTerminalState(
  isLoading: boolean,
  authStatus: AuthStatus,
  responseStatus: string | undefined
): TerminalState {
  if (isLoading) {
    return "loading";
  }
  if (authStatus === "pending") {
    return "loading";
  }
  if (authStatus === "failed") {
    return "error";
  }
  if (responseStatus) {
    return responseStatus === "error" ? "error" : "success";
  }
  return "ready";
}

export function ResponsePanel({
  onExecute,
  isExecuteDisabled,
  expectedOutput,
  tooltipMessage,
  onBackToTool,
}: ResponsePanelProps) {
  const { toolResponse, isLoading, authStatus, selectedTool } =
    usePlaygroundStore(
      useShallow((state) => ({
        toolResponse: state.response,
        isLoading: state.isLoading,
        authStatus: state.authStatus,
        selectedTool: state.selectedTool,
      }))
    );

  const needsAuthorization = checkNeedsOAuthAuthorization(
    selectedTool?.requirements?.authorization,
    authStatus === "failed" ? "error" : (authStatus ?? undefined)
  );

  const terminalState = deriveTerminalState(
    isLoading,
    authStatus,
    toolResponse?.status
  );

  return (
    <TerminalOutput
      expectedOutput={expectedOutput}
      isAuthError={authStatus === "failed"}
      isExecuteDisabled={isExecuteDisabled}
      needsAuthorization={needsAuthorization}
      onBackToTool={onBackToTool}
      onExecute={onExecute}
      response={toolResponse}
      state={terminalState}
      toolName={selectedTool?.fully_qualified_name ?? undefined}
      tooltipMessage={tooltipMessage}
    />
  );
}
